import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet, fetchApiPost } from "./utils";

export const GenerateAgtechReportBodyValidation = z.object({
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export interface GenerateAgtechReportResponse {
  document_id: string;
  presigned_get_url: string;
}

export const GenerateAgtechShippedReportValidation = z.object({
  filters: z.object({
    customer_ids: z.string().optional(),
    commodity_id: z.string().optional(),
    ship_to: z.string().optional(),
    ship_date_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    ship_date_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  report_info: z.object({
    title: z.string().trim().min(1).max(250).optional(),
    tab_name: z
      .string()
      .trim()
      .min(1)
      .max(31)
      .refine(
        (value: string): boolean => !/[\\/?*[\]:]/.test(value),
        "Tab name cannot contain \\, /, ?, *, [, ], or :",
      )
      .optional(),
  }).optional(),
});

export type GenerateAgtechShippedReportRequest = z.infer<typeof GenerateAgtechShippedReportValidation>;

export interface GenerateAgtechShippedReportResponse {
  link: string;
  filename: string;
}

export interface QueueStats {
    name: string;
    type: string;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    halted: number;
    oldest_pending: Date | null | undefined;
}

export function useGenerateAgtechReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: z.infer<typeof GenerateAgtechReportBodyValidation>) => {
      return fetchApiPost<GenerateAgtechReportResponse>(
        "contracts/reports/release/generate",
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agtech", "reports"] });
    },
  });
}

export function useGenerateAgtechShippedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GenerateAgtechShippedReportRequest) => {
      const queryParams = new URLSearchParams();
      if (data.filters.customer_ids) queryParams.append("customer_ids", data.filters.customer_ids);
      if (data.filters.commodity_id) queryParams.append("commodity_id", data.filters.commodity_id);
      if (data.filters.ship_to) queryParams.append("ship_to", data.filters.ship_to);
      if (data.filters.ship_date_start) queryParams.append("ship_date_start", data.filters.ship_date_start);
      if (data.filters.ship_date_end) queryParams.append("ship_date_end", data.filters.ship_date_end);

      return fetchApiPost<GenerateAgtechShippedReportResponse>(
        "agtech/tickets/reports/shipped",
        { report_info: data.report_info },
        queryParams,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agtech", "reports"] });
    },
  });
}

export function useGetAgtechSyncStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["agtech", "reports", "stats"],
    queryFn: () => fetchApiGet<QueueStats>("contracts/reports/stats"),
    retry: 1,
    enabled: options?.enabled,
  });
}
